import { ec, ecId, handBack, insertRoll, itemField, record, scalar, upper, type JanusCtx, type Row } from '../ability/context.js';
import { statusItemId } from './expiry.js';
import { readItemField } from '../compile/item-field-reader.js';
import { EQUIPMENT_SLOTS } from '../inventory/equipment-slots.js';
import { heldInventory } from '../inventory/held-inventory.js';
import { appendFormInventoryWrites, formInventoryField } from './shapeshift-transition.js';
import { projectShapeshiftFlag } from './shapeshift-state.js';

export type ShapeshiftInventoryWrite =
  | { kind: 'absorb'; inventory_id: string; before: 0 }
  | { kind: 'restore_absorbed'; inventory_id: string; before: 2 }
  | { kind: 'unequip_acquired'; inventory_id: string; before: string };

const UUID = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i;

/** Admit the final storage plan once, before its caller can draw dice or change held state. */
function admitInventoryWrites(writes: ShapeshiftInventoryWrite[], phase: 'shapeshift' | 'lost_shapeshift'): ShapeshiftInventoryWrite[] {
  if (writes.length > 128) handBack(`${phase}:inventory-write-limit`);
  const seen = new Set<string>();
  for (const write of writes) {
    const column = write.kind === 'unequip_acquired' ? 'equipped' : 'inventory_private';
    const identity = `${upper(write.inventory_id)}:${column}`;
    if (!UUID.test(write.inventory_id) || seen.has(identity)
      || (write.kind === 'unequip_acquired' ? typeof write.before !== 'string' || !UUID.test(write.before)
        : write.before !== (write.kind === 'absorb' ? 0 : 2))) handBack(`${phase}:inventory-not-held`);
    seen.add(identity);
  }
  return writes;
}

/** Only direct, undeleted, public rows disappear into the form; nested container contents retain their privacy. */
export function absorbShapeshiftInventory(ctx: JanusCtx, target: Row): ShapeshiftInventoryWrite[] {
  const inventory = heldInventory(target, ctx.read.value);
  if (inventory === null) handBack('shapeshift:inventory-not-held');
  const read = ctx.read.inventoryField;
  if (!read) handBack('shapeshift:inventory-reader-not-held');
  const writes: ShapeshiftInventoryWrite[] = [];
  for (const entry of inventory) {
    const row = record(entry);
    if (!row) handBack('shapeshift:inventory-not-held');
    const possessor = upper(read(row, 'possess_id'));
    if (!possessor) handBack('shapeshift:inventory-owner-not-held');
    if (possessor !== ecId(target) || Number(read(row, 'inventory_deleted') ?? 0) !== 0) continue;
    const privacy = Number(formInventoryField(ctx, row, 'inventory_private') ?? 0);
    if (!Number.isInteger(privacy) || privacy < 0 || privacy > 2) handBack('shapeshift:inventory-privacy-not-held');
    if (privacy !== 0) continue;
    const id = upper(read(row, 'inventory_id'));
    writes.push({ kind: 'absorb', inventory_id: id, before: 0 });
  }
  return admitInventoryWrites(writes, 'shapeshift');
}

/** lost_shapeshift: absorbed gear returns; public gear sharing an occupied slot is unequipped. */
export function shapeshiftInventory(ctx: JanusCtx, target: Row): ShapeshiftInventoryWrite[] {
  const entries = heldInventory(target, ctx.read.value);
  if (entries === null) handBack('lost_shapeshift:inventory-not-held');
  const read = ctx.read.inventoryField;
  if (!read && entries.length) handBack('lost_shapeshift:inventory-reader-not-held');
  const value = (row: Row, key: string): unknown => scalar(read?.(row, key));
  return formInventoryWrites(target, entries, value, ctx.read.value);
}

/** Shared held-row plan for command preparation and the cluster projection. */
export function formInventoryWrites(target: Row, entries: readonly unknown[], value: (row: Row, key: string) => unknown,
  layer: (row: Row, key: string) => unknown): ShapeshiftInventoryWrite[] {
  const inventory = entries.map(entry => {
    const row = record(entry);
    if (!row) handBack('lost_shapeshift:inventory-not-held');
    return row;
  });
  const slots = new Set<string>(Object.values(EQUIPMENT_SLOTS));
  const direct = inventory.filter(row => upper(value(row, 'possess_id')) === ecId(target)
    && Number(value(row, 'inventory_deleted') ?? 0) === 0);
  const occupied = new Set(direct.filter(row => Number(value(row, 'inventory_private') ?? 0) === 2)
    .map(row => upper(value(row, 'equipped') ?? layer(row, 'equipped'))));
  const writes: ShapeshiftInventoryWrite[] = [];
  for (const row of direct) {
    const privacy = Number(value(row, 'inventory_private') ?? 0);
    const slot = upper(value(row, 'equipped') ?? layer(row, 'equipped'));
    if (!Number.isInteger(privacy) || privacy < 0 || privacy > 2) handBack('lost_shapeshift:inventory-privacy-not-held');
    const id = upper(value(row, 'inventory_id'));
    const restore = privacy === 2;
    const unequip = privacy === 0 && slots.has(slot) && occupied.has(slot);
    if (!restore && !unequip) continue;
    if (restore) writes.push({ kind: 'restore_absorbed', inventory_id: id, before: 2 });
    if (unequip) writes.push({ kind: 'unequip_acquired', inventory_id: id, before: String(value(row, 'equipped') ?? '') });
  }
  return admitInventoryWrites(writes, 'lost_shapeshift');
}

/** Decide the exact saved rows before mutation/dice. Projection availability is
 * an apply-time concern: incomplete sources use the existing member recovery. */
export function planFormStatusRemoval(ctx: JanusCtx, target: Row, status: Row): ShapeshiftInventoryWrite[] {
  if (!ctx.saveBoundaryInventory && ctx.shapeshiftInventory === undefined) handBack('lost_shapeshift:lane-cannot-persist');
  return shapeshiftInventory(ctx, target);
}

/** Called before this exact form row's loss hook; never selects a different form or walks a container's contents. */
export function prepareFormStatusRemoval(ctx: JanusCtx, target: Row, status: Row): void {
  if (!isFormStatus(ctx, status)) return;
  // Loss hooks see the flag for the remaining held rows. Missing definitions
  // leave it intact and use the browser lane's exact-definition refusal.
  const reason = projectShapeshiftFlag(target, ctx.read.definition, String(readItemField(status, 'status_id') ?? ''));
  if (reason) handBack(reason);
  if (itemField(target, 'shapeshifted')) return;
  const writes = planFormStatusRemoval(ctx, target, status);
  appendFormInventoryWrites(ctx, writes);
  ec(target).v_temp = 0;
  const vitality = record(record(target.computed)?.vitality);
  if (vitality) { vitality.v_temp = 0; vitality.base_v_temp = 0; }
  insertRoll(ctx, { roll_type_1: 'lost_shapeshift', by: target });
}

export function isFormStatus(ctx: JanusCtx, status: Row): boolean {
  return String(itemField(ctx.read.definition(statusItemId(status)) ?? record(status.status_arr), 'item_type')) === 'creature';
}
